import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Provider } from '../../../models/Provider'
import useProviderSuggestions from '../../../state/queries/useProviderSuggestions'
import useDialog from '../../../utilities/useDialog'
import Button from '../../basics/Button/Button'
import singleItemSelect, { formikConnectedSingleItemSelect } from '../../basics/ItemSelect/singleItemSelect'
import EditProviderForm from '../../EditProviderForm/EditProviderForm'

import styles from './SelectProvider.module.scss'

/**********************************************************************
 * SelectProvider
 **********************************************************************/
const SelectProviderCombobox = singleItemSelect({
  useSuggestions: useProviderSuggestions,
  renderItemDescription: (provider) => provider.name,
})

const SelectProvider = React.memo(function SelectProvider({
  allowCreate,
  ...props
}: { allowCreate?: boolean } & React.ComponentProps<typeof SelectProviderCombobox>) {
  const { t } = useTranslation()
  const showDialog = useDialog()

  const { setItem } = props
  const handleCreateProvider = useCallback(async () => {
    const provider = await showDialog(
      (close) => <EditProviderForm onClose={close} />,
      () => undefined as Provider | undefined
    )
    if (provider) {
      setItem(provider)
    }
  }, [setItem])

  return (
    <div className={styles.SelectProvider}>
      <SelectProviderCombobox {...props} />
      {allowCreate && (
        <Button small onClick={handleCreateProvider}>
          {t('provider.cta create')}
        </Button>
      )}
    </div>
  )
})

export default SelectProvider

/**********************************************************************
 * SelectProviderFormik
 **********************************************************************/
export const _SelectProviderFormik = formikConnectedSingleItemSelect({
  component: SelectProvider,
})

export const SelectProviderFormik = _SelectProviderFormik as React.FunctionComponent<
  React.ComponentProps<typeof _SelectProviderFormik> & { allowCreate?: boolean }
>
